import { useTranslation } from "react-i18next";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import { Employment } from "../../../../employment/employmentApi";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { TargetedShiftsForm } from "./TargetedShiftsForm";
import { useAppSelector } from "../../../../../app/hooks";
import { NameIdOption } from "../../../../../common/types/commonTypes";
import {
  TargetedShiftSpec,
  TargetingKeys,
} from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";

type Props = {
  employment: Employment;
  canEdit: boolean;
  infoTooltip?: string;
  initialSpec?: TargetedShiftSpec;
  onRulesChanged?: (count: number) => void;
};

export type TargetingRuleValues = {
  id?: string;
  customerCompany: NameIdOption | null;
  customerOrganization: NameIdOption | null;
  workLocations: NameIdOption[];
  workTitles: NameIdOption[];
  isDefaultRule: boolean;
};

export type FormValues = {
  selectedAssignment: string;
  rules: TargetingRuleValues[];
  version: number;
};

export const TargetedShifts = ({
  employment,
  canEdit,
  infoTooltip,
  initialSpec,
  onRulesChanged,
}: Props) => {
  const { t } = useTranslation("talentProfile");
  const { workTitle } = useAppSelector((state) => state.localized);

  const defaultValues = {
    selectedAssignment: "",
    rules: [],
    version: 0,
  };

  const methods = useForm<FormValues>({ defaultValues, mode: "onChange" });

  const { setValue } = methods;

  useEffect(() => {
    if (!initialSpec) return;
    const rules = initialSpec.targetingRules.map(
      (rule: TargetingKeys, i: number) => ({
        customerCompany: rule.custCompany ?? null,
        customerOrganization: rule.custOrgs[0] ?? null,
        workLocations: rule.workLocs ?? [],
        workTitles: rule.workTitles.map((id) => ({
          id,
          name: workTitle[id] || "",
        })),
        isDefaultRule: i === 0,
      }),
    );
    setValue("rules", rules);
    setValue("version", initialSpec.version || 0);
    setValue(
      "selectedAssignment",
      initialSpec.variationValidationAssignmentId || "",
    );
  }, [initialSpec, setValue, workTitle]);

  return !employment.isShift ? (
    <FormSection
      title={t("targetedShifts.targetedShifts")}
      readOnly={!canEdit}
      infoTooltip={
        infoTooltip ? (
          <InfoTooltip title={infoTooltip} style={{ marginLeft: 2 }} />
        ) : undefined
      }
    >
      <Grid item container xs={12}>
        <FormProvider {...methods}>
          <TargetedShiftsForm onRulesChanged={onRulesChanged} />
        </FormProvider>
      </Grid>
    </FormSection>
  ) : null;
};
